import React, { useState, useEffect, useCallback } from 'react';
import { crmAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import {
  Activity, Search, Clock, CheckCircle, AlertTriangle, Calendar,
  ChevronRight, ChevronDown, Zap, Building2, User, Link2,
  RefreshCw, DollarSign, Target, ArrowUpDown, X, BarChart2
} from 'lucide-react';
import { SalesCadenceView } from './SalesCadenceView';
import { CompactBusinessRules } from '../layout/CompactBusinessRules';

const SALES_ACTIVITY_TYPES = new Set([
  "Demo", "RFP Submission", "Proof of concept",
  "Vendor Meeting", "Product Presentation", "POC", "Vendor Presentation"
]);

const TYPE_COLOR = {
  demo: 'bg-blue-100 text-blue-700',
  rfp_submission: 'bg-rose-100 text-rose-700',
  'rfp submission': 'bg-rose-100 text-rose-700',
  vendor_meeting: 'bg-teal-100 text-teal-700',
  'vendor meeting': 'bg-teal-100 text-teal-700',
  'proof of concept': 'bg-cyan-100 text-cyan-700',
  proof_of_concept: 'bg-cyan-100 text-cyan-700',
  product_presentation: 'bg-violet-100 text-violet-700',
  poc: 'bg-cyan-100 text-cyan-700',
  vendor_presentation: 'bg-violet-100 text-violet-700',
  call: 'bg-blue-50 text-blue-600',
  email: 'bg-purple-50 text-purple-600',
  meeting: 'bg-amber-50 text-amber-600',
  to_do: 'bg-gray-100 text-gray-600',
  'to do': 'bg-gray-100 text-gray-600',
};

function getTypeColor(type) {
  const key = (type || '').toLowerCase().replace(/ /g, '_');
  return TYPE_COLOR[key] || TYPE_COLOR[(type || '').toLowerCase()] || 'bg-gray-100 text-gray-600';
}

function SalesActivityBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#800000]/10 text-[#800000] border border-[#800000]/20">
      <Zap className="h-2.5 w-2.5" /> Sales Activity
    </span>
  );
}

function ActivityRow({ activity }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleExpand = async () => {
    if (!expanded && !detail) {
      setLoadingDetail(true);
      try { const r = await crmAPI.getActivityDetail(activity.id); setDetail(r.data); }
      catch { setDetail({}); }
      finally { setLoadingDetail(false); }
    }
    setExpanded(!expanded);
  };

  const isSales = activity.is_sales_activity || SALES_ACTIVITY_TYPES.has(activity.type_display || activity.type);
  const typeColor = getTypeColor(activity.type_display || activity.type);

  const now = new Date();
  const dueDate = activity.due_date ? new Date(activity.due_date) : null;
  let status = activity.status || 'planned';
  if (status !== 'completed' && status !== 'cancelled') {
    if (dueDate && dueDate < now) status = 'overdue';
    else if (dueDate && (dueDate - now) / (1000*60*60*24) <= 1) status = 'today';
    else status = 'planned';
  }

  const statusCfg = {
    completed: 'bg-emerald-100 text-emerald-700',
    overdue: 'bg-red-100 text-red-700',
    today: 'bg-amber-100 text-amber-700',
    planned: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };

  const LinkedRecord = ({ linked }) => {
    if (!linked) return null;
    const icons = { opportunity: Target, invoice: DollarSign, account: Building2 };
    const Icon = icons[linked.model] || Link2;
    return (
      <div className="flex items-center gap-1.5 p-2 rounded bg-gray-50 border text-xs mt-1">
        <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
        <div className="min-w-0">
          <p className="text-[9px] text-gray-400 uppercase font-bold">{linked.label}</p>
          <p className="text-xs font-semibold truncate">{linked.name || linked.invoice_number}</p>
          {linked.account_name && <p className="text-[10px] text-gray-400 truncate">{linked.account_name}</p>}
          {linked.stage && <Badge className="text-[8px] bg-blue-50 text-blue-600 border-0 mt-0.5">{linked.stage}</Badge>}
        </div>
      </div>
    );
  };

  return (
    <>
      <tr
        onClick={handleExpand}
        className={`border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50/50 ${expanded ? 'bg-gray-50/30' : ''} ${isSales ? 'border-l-2 border-l-[#800000]' : ''}`}
        data-testid={`activity-row-${activity.id}`}
      >
        <td className="px-3 py-3 w-6">
          <ChevronRight className={`h-3.5 w-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </td>
        <td className="px-2 py-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${typeColor}`}>
              {activity.type_display || activity.type || 'Activity'}
            </span>
            {isSales && <Zap className="h-3 w-3 text-[#800000]" />}
          </div>
        </td>
        <td className="px-2 py-3">
          <div>
            <p className="text-sm font-medium text-gray-900 truncate max-w-[250px]">{activity.subject || 'Activity'}</p>
            {activity.description && <p className="text-[10px] text-gray-400 truncate max-w-[200px] hidden lg:block">{activity.description.slice(0, 60)}</p>}
          </div>
        </td>
        <td className="px-2 py-3">
          {activity.opportunity_name ? (
            <p className="text-xs text-[#800000] font-medium truncate max-w-[160px]" title={activity.opportunity_name}>
              {activity.opportunity_name}
            </p>
          ) : <span className="text-xs text-gray-300">—</span>}
        </td>
        <td className="px-2 py-3">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#800000] to-rose-400 flex items-center justify-center text-white text-[8px] font-bold shrink-0">
              {(activity.assigned_user || activity.owner_name || '?').charAt(0).toUpperCase()}
            </div>
            <span className="text-xs text-gray-700 truncate max-w-[120px]">
              {activity.assigned_user || activity.owner_name || 'Unassigned'}
            </span>
          </div>
        </td>
        <td className="px-2 py-3">
          <span className="text-xs text-gray-600 font-mono">{activity.due_date?.split('T')[0]?.split(' ')[0] || '—'}</span>
        </td>
        <td className="px-2 py-3">
          <Badge className={`text-[10px] ${statusCfg[status] || 'bg-blue-100 text-blue-700'}`}>{status}</Badge>
        </td>
      </tr>

      {expanded && (
        <tr className="border-b border-gray-100 bg-gray-50/50" data-testid={`activity-detail-${activity.id}`}>
          <td colSpan={7} className="px-4 py-4">
            {loadingDetail ? (
              <div className="text-xs text-gray-400 flex items-center gap-2">
                <div className="animate-spin h-3 w-3 border border-gray-400 border-t-transparent rounded-full" />
                Loading details...
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">People</p>
                  <div className="flex items-center gap-2 text-xs">
                    <User className="h-3.5 w-3.5 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-400">Assigned To</p>
                      <p className="font-semibold text-gray-800">{detail?.assigned_user || activity.assigned_user || 'Unassigned'}</p>
                    </div>
                  </div>
                  {(detail?.created_by || activity.created_by) && (
                    <div className="flex items-center gap-2 text-xs">
                      <User className="h-3.5 w-3.5 text-gray-400" />
                      <div>
                        <p className="text-[10px] text-gray-400">Created By</p>
                        <p className="text-gray-700">{detail?.creator_info?.name || detail?.created_by || activity.created_by}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Timeline</p>
                  {(detail?.date_deadline || activity.due_date) && (
                    <div className="flex items-center gap-2 text-xs">
                      <Clock className="h-3.5 w-3.5 text-gray-400" />
                      <div>
                        <p className="text-[10px] text-gray-400">Due Date</p>
                        <p className="text-gray-800">{(detail?.date_deadline || activity.due_date)?.split('T')[0]?.split(' ')[0]}</p>
                      </div>
                    </div>
                  )}
                  {isSales && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <SalesActivityBadge />
                      <span className="text-[10px] text-gray-400">Counted for KPI</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <Link2 className="h-3 w-3" /> Linked Record
                  </p>
                  {detail?.linked_record ? (
                    <LinkedRecord linked={detail.linked_record} />
                  ) : (activity.opportunity_name || activity.res_name) ? (
                    <div className="p-2 rounded bg-gray-50 border text-xs">
                      <p className="text-[9px] text-gray-400">Opportunity</p>
                      <p className="font-semibold truncate">{activity.opportunity_name || activity.res_name}</p>
                    </div>
                  ) : <p className="text-xs text-gray-400 italic">No linked record</p>}
                </div>
                {(detail?.note || detail?.description || activity.description) && (
                  <div className="md:col-span-3 pt-2 border-t border-gray-100">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Notes</p>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">
                      {String(detail?.note || detail?.description || activity.description || '').replace(/<[^>]+>/g, ' ').trim()}
                    </p>
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function ActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState(null); // null = All Years for activities (all completed activities visible)
  const [quarterFilter, setQuarterFilter] = useState(null);
  const [salesOnly, setSalesOnly] = useState(false);
  const [typeFilters, setTypeFilters] = useState(new Set()); // multi-select type filter
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (yearFilter) params.year = yearFilter;
      if (quarterFilter) params.quarter = quarterFilter;
      const res = await crmAPI.listActivities(params);
      setActivities(res.data || []);
    } catch { toast.error('Failed to load activities'); }
    finally { setLoading(false); }
  }, [yearFilter, quarterFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const now = new Date();

  const getStatus = (a) => {
    return a.status || 'planned';
  };

  // All unique types for the filter bar
  const allTypes = [...new Set(activities.map(a => a.type_display || a.type).filter(Boolean))].sort();

  const toggleTypeFilter = (type) => {
    setTypeFilters(prev => {
      const n = new Set(prev);
      n.has(type) ? n.delete(type) : n.add(type);
      return n;
    });
  };

  const clearTypeFilters = () => setTypeFilters(new Set());

  // Filter chain
  let filtered = activities.filter(a => {
    const s = getStatus(a);
    if (statusTab !== 'all' && s !== statusTab) return false;
    if (salesOnly && !(a.is_sales_activity || SALES_ACTIVITY_TYPES.has(a.type_display || a.type))) return false;
    if (typeFilters.size > 0 && !typeFilters.has(a.type_display) && !typeFilters.has(a.type)) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return [a.subject, a.type_display, a.assigned_user, a.owner_name, a.opportunity_name, a.description]
        .some(v => v && String(v).toLowerCase().includes(q));
    }
    return true;
  });

  // Sort
  if (sortField) {
    filtered = [...filtered].sort((a, b) => {
      let av = a[sortField] || ''; let bv = b[sortField] || '';
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      return sortDir === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
    });
  }

  const stats = {
    all: activities.length,
    planned: activities.filter(a => getStatus(a) === 'planned').length,
    today: activities.filter(a => getStatus(a) === 'today').length,
    completed: activities.filter(a => getStatus(a) === 'completed').length,
    overdue: activities.filter(a => getStatus(a) === 'overdue').length,
  };
  const salesCount = activities.filter(a => a.is_sales_activity || SALES_ACTIVITY_TYPES.has(a.type_display || a.type)).length;

  const SortableHeader = ({ label, field, className = '' }) => (
    <th
      className={`px-2 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-[#800000] select-none group ${className}`}
      onClick={() => { setSortField(field); setSortDir(prev => sortField === field && prev === 'asc' ? 'desc' : 'asc'); }}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${sortField === field ? 'text-[#800000]' : 'text-gray-300 group-hover:text-gray-400'}`} />
      </div>
    </th>
  );

  if (loading) return (
    <div className="space-y-4 p-1">
      <div className="grid grid-cols-5 gap-3">{[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />)}</div>
      <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
    </div>
  );

  return (
    <div className="space-y-4 p-1" data-testid="activities-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="page-header-title flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#800000]" /> Activities
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {activities.length} unified activities · {salesCount} sales activity types
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CompactBusinessRules
            scopeItems={[yearFilter, quarterFilter, salesOnly ? 'Sales activity types only' : null].filter(Boolean)}
            ruleKeys={['totals_scope']}
            label="Feed Rules"
            dataTestId="activities-compact-business-rules"
          />
          <Select value={yearFilter || 'all'} onValueChange={v => setYearFilter(v === 'all' ? null : v)}>
            <SelectTrigger className="w-[100px] h-8 text-xs" data-testid="year-filter">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {['2026', '2025', '2024'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={quarterFilter || 'all'} onValueChange={v => setQuarterFilter(v === 'all' ? null : v)}>
            <SelectTrigger className="w-[100px] h-8 text-xs" data-testid="quarter-filter">
              <SelectValue placeholder="Quarter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Qtrs</SelectItem>
              {['Q1', 'Q2', 'Q3', 'Q4'].map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadData} className="h-8 w-8 p-0">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="activities-tab-list">
            <Activity className="h-3.5 w-3.5 mr-1.5" />Activity Feed ({activities.length})
          </TabsTrigger>
          <TabsTrigger value="cadence" data-testid="activities-tab-cadence">
            <BarChart2 className="h-3.5 w-3.5 mr-1.5" />Cadence & Assignments
          </TabsTrigger>
        </TabsList>

        {/* ── ACTIVITY LOG TAB ── */}
        <TabsContent value="list" className="mt-4 space-y-4">
      {/* Stats cards */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { key: 'all', label: 'Total', value: stats.all, color: 'text-gray-700', bg: 'bg-white' },
          { key: 'planned', label: 'Planned', value: stats.planned, color: 'text-blue-600', bg: 'bg-blue-50' },
          { key: 'today', label: "Today", value: stats.today, color: 'text-amber-600', bg: 'bg-amber-50' },
          { key: 'completed', label: 'Completed', value: stats.completed, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { key: 'overdue', label: 'Overdue', value: stats.overdue, color: 'text-red-600', bg: 'bg-red-50' },
        ].map(s => (
          <div key={s.key}
            onClick={() => setStatusTab(statusTab === s.key ? 'all' : s.key)}
            className={`cursor-pointer rounded-lg border p-3 text-center transition-all hover:shadow-sm ${statusTab === s.key ? 'ring-2 ring-[#800000] ring-offset-1' : ''} ${s.bg}`}
            data-testid={`status-card-${s.key}`}>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            {statusTab === s.key && s.key !== 'all' && <div className="w-4 h-0.5 bg-[#800000] mx-auto mt-1 rounded" />}
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="space-y-2">
        {/* Row 1: Search + Sales Only toggle */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative min-w-[200px] flex-1 max-w-[300px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search activity feed..." className="pl-8 h-8 text-xs" data-testid="search-input" />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={() => setSalesOnly(!salesOnly)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${salesOnly ? 'bg-[#800000] text-white border-[#800000]' : 'bg-[#800000]/5 text-[#800000] border-[#800000]/20 hover:bg-[#800000]/10'}`}
            data-testid="sales-only-toggle"
          >
            <Zap className="h-3 w-3" /> Sales Activity Types Only ({salesCount})
          </button>
          {(typeFilters.size > 0 || salesOnly || statusTab !== 'all' || searchQuery || yearFilter || quarterFilter) && (
            <button
              onClick={() => { setTypeFilters(new Set()); setSalesOnly(false); setStatusTab('all'); setSearchQuery(''); setYearFilter(null); setQuarterFilter(null); }}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
            >
              <X className="h-3 w-3" /> Clear all filters
            </button>
          )}
        </div>

        {/* Row 2: Type filter pills */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Filter by type:</span>
          {allTypes.map(type => {
            const isSalesType = SALES_ACTIVITY_TYPES.has(type);
            const isActive = typeFilters.has(type);
            const typeColor = getTypeColor(type);
            return (
              <button
                key={type}
                onClick={() => toggleTypeFilter(type)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all ${isActive ? `${typeColor} border-current ring-1 ring-current` : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'}`}
                data-testid={`type-filter-${type.toLowerCase().replace(/ /g, '-')}`}
              >
                {isSalesType && <Zap className="h-2.5 w-2.5" />}
                {type}
              </button>
            );
          })}
          {typeFilters.size > 0 && (
            <button onClick={clearTypeFilters} className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5">
              <X className="h-3 w-3" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Active filter summary */}
      {(typeFilters.size > 0 || salesOnly || statusTab !== 'all' || yearFilter || quarterFilter) && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="font-medium">Showing:</span>
          {yearFilter && <Badge className="text-[10px] bg-slate-100 text-slate-700 border-0">{yearFilter}</Badge>}
          {quarterFilter && <Badge className="text-[10px] bg-slate-100 text-slate-700 border-0">{quarterFilter}</Badge>}
          {statusTab !== 'all' && <Badge className="text-[10px] bg-blue-50 text-blue-700 border-0">{statusTab}</Badge>}
          {salesOnly && <Badge className="text-[10px] bg-[#800000]/10 text-[#800000] border-0">Sales Activity Types</Badge>}
          {[...typeFilters].map(t => <Badge key={t} className="text-[10px] bg-gray-100 text-gray-700 border-0">{t}</Badge>)}
          <span className="text-gray-400">— {filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="activities-table">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="w-6 px-3 py-3"></th>
              <SortableHeader label="Type" field="type_display" />
              <SortableHeader label="Subject" field="subject" />
              <SortableHeader label="Opportunity" field="opportunity_name" />
              <SortableHeader label="Assigned To" field="assigned_user" />
              <SortableHeader label="Due Date" field="due_date" />
              <SortableHeader label="Status" field="status" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-gray-400 text-sm">
                No activities found
                {typeFilters.size > 0 && <span> for selected types</span>}
              </td></tr>
            ) : filtered.map(activity => (
              <ActivityRow key={activity.id} activity={activity} />
            ))}
          </tbody>
        </table>
      </div>
        </TabsContent>

        {/* ── CADENCE TAB ── */}
        <TabsContent value="cadence" className="mt-4">
          <SalesCadenceView
            externalYearFilter={yearFilter || 'all'}
            externalQuarterFilter={quarterFilter || 'all'}
            externalSalesOnly={salesOnly}
            onYearFilterChange={(v) => setYearFilter(v === 'all' ? null : v)}
            onQuarterFilterChange={(v) => setQuarterFilter(v === 'all' ? null : v)}
            onSalesOnlyChange={(v) => setSalesOnly(v)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
